import React from 'react';
import { atom, useAtom, useSetAtom } from 'jotai';
import { selectAtom } from 'jotai/utils';
import { useCallback, useEffect, useRef } from 'react';
import type {
  RaptorDraftQuery,
  RaptorDraftQueryVariables,
} from '../../graphql';
import { RaptorDraftDocument } from '../../graphql';
import { apolloClient } from '../../lib/init-apollo';
import type { ScenarioLocation } from '../../lib/planningData';
import { hasFeature } from '../../state/zustand';
import type { ClientId } from '../core/ClientId';
import type { EventTimeline } from '../core/Timelines';
import LoadingSpinner from '../LoadingSpinner';
import { Box, Text, Tooltip } from '../ui';
import type {
  Suggestion,
  SuggestionResult,
} from '../core/suggestions/getSuggestions';
import type { Vessel } from '../core/Vessel';
import { isLoadEventTimeData } from '../core/TimeData';

type DraftParams = {
  loadedMass: number;
  portCallId: ClientId;
  portDraft: number;
  portName: string;
  vesselImo: number;
};

type DraftIssue = {
  draft: {
    fit: number;
    upr: number;
    lwr: number;
  };
  isDraftOverLimit: boolean;
  params: DraftParams;
};

function getPossibleDraftIssues(
  timelines: EventTimeline[],
  locationsById: Map<number, ScenarioLocation>,
) {
  const possibleIssues: DraftParams[] = [];

  timelines.forEach((timeline) => {
    const vesselDraft = timeline.vessel?.draft;
    const vesselImo = timeline.vessel?.imo;
    if (timeline.type !== 'tc' || !vesselDraft || !vesselImo) return;

    timeline.voyageGroups.forEach((vg) =>
      vg.portCalls.forEach((pc) => {
        const location = locationsById.get(pc.locationId);
        if (!pc.id || !location?.draft || +vesselDraft <= +location.draft) {
          return;
        }

        /*
         * Check used mass at first and last event of port call as those are
         * the ones used when entering and leaving the port
         */
        const portCallMaxMass = Math.max(
          pc.tds.at(0)?.usedMassCapacity ?? 0,
          pc.tds.at(-1)?.usedMassCapacity ?? 0,
        );

        possibleIssues.push({
          loadedMass: portCallMaxMass,
          portCallId: pc.id,
          portDraft: +location.draft,
          portName: location.name,
          vesselImo: +vesselImo,
        });
      }),
    );
  });

  return possibleIssues;
}

const checkedParams = new Set<string>();
function stringifyParams(params: DraftParams) {
  return `${params.vesselImo}-${params.portCallId}-${params.loadedMass}`;
}

const draftIssuesAtom = atom<DraftIssue[]>([]);
const addDraftIssueAtom = atom(null, (get, set, issue: DraftIssue) => {
  const currentIssues = get(draftIssuesAtom);
  set(draftIssuesAtom, [...currentIssues, issue]);
});

const queueAtom = atom<DraftParams[]>([]);
const queuePossibleDraftIssuesAtom = atom(
  null,
  (
    get,
    set,
    timelines: EventTimeline[],
    locationsById: Map<number, ScenarioLocation>,
  ) => {
    const possibleIssues = getPossibleDraftIssues(timelines, locationsById);
    const filteredIssues = possibleIssues.filter(
      (params) => !checkedParams.has(stringifyParams(params)),
    );

    const currentQueue = get(queueAtom);
    set(queueAtom, [...currentQueue, ...filteredIssues]);

    /** Cache checked params to avoid refetching */
    filteredIssues.forEach((params) =>
      checkedParams.add(stringifyParams(params)),
    );
  },
);
const queueLengthAtom = selectAtom(queueAtom, (queue) => queue.length);

type FetchError = {
  message: string;
  params: DraftParams;
};
const errorsAtom = atom<FetchError[]>([]);
const addErrorAtom = atom(null, (get, set, err: FetchError) => {
  set(errorsAtom, [...get(errorsAtom), err]);
});

async function fetchRaptorDraft(vesselImo: number, loadedMass: number) {
  if (!apolloClient) {
    return Promise.reject('Apollo not initialized');
  }

  const res = await apolloClient.query<
    RaptorDraftQuery,
    RaptorDraftQueryVariables
  >({
    query: RaptorDraftDocument,
    variables: {
      imo: vesselImo,
      loadedMass: loadedMass,
    },
  });

  const draft = res.data.getRaptorDraft;

  if (
    typeof draft?.fit !== 'number' ||
    typeof draft?.upr !== 'number' ||
    typeof draft?.lwr !== 'number'
  ) {
    throw new Error('Bad response');
  }

  return {
    fit: draft.fit,
    lwr: draft.lwr,
    upr: draft.upr,
  };
}

type DraftQueueProps = {
  timelines: EventTimeline[];
  locationsById: Map<number, ScenarioLocation>;
};

function DraftQueue(props: DraftQueueProps) {
  const isProcessing = useRef(false);
  const [queue, setQueue] = useAtom(queueAtom);
  const queuePossibleDraftIssues = useSetAtom(queuePossibleDraftIssuesAtom);
  const addDraftIssue = useSetAtom(addDraftIssueAtom);
  const addError = useSetAtom(addErrorAtom);

  /* Check timelines for potential issues and queue them */
  useEffect(() => {
    queuePossibleDraftIssues(props.timelines, props.locationsById);
  }, [props.timelines, props.locationsById, queuePossibleDraftIssues]);

  /* Process queue */
  useEffect(() => {
    if (isProcessing.current || queue.length < 1) return;

    isProcessing.current = true;
    const [paramsToProcess, ...rest] = queue;

    async function asyncFn() {
      try {
        const draft = await fetchRaptorDraft(
          paramsToProcess.vesselImo,
          paramsToProcess.loadedMass,
        );

        addDraftIssue({
          draft,
          isDraftOverLimit: draft.fit > paramsToProcess.portDraft,
          params: paramsToProcess,
        });
      } catch (err) {
        addError({ message: err.message, params: paramsToProcess });
        console.error('Raptor draft error', err, paramsToProcess);
      }

      isProcessing.current = false;
      setQueue(rest);
    }

    asyncFn();
  }, [addDraftIssue, addError, setQueue, queue]);

  return null;
}

export function IntegrationRaptorDraft(props: DraftQueueProps) {
  const isEnabled = hasFeature('raptor_draft');
  if (!isEnabled) return null;

  return (
    <DraftQueue
      timelines={props.timelines}
      locationsById={props.locationsById}
    />
  );
}

export function useIntegrationRaptorDraftViolations(
  portCallIds: Set<ClientId>,
) {
  const selector = useCallback(
    (draftIssues: DraftIssue[]) =>
      draftIssues.filter((issue) => portCallIds.has(issue.params.portCallId)),
    [portCallIds],
  );

  const [draftViolations] = useAtom(selectAtom(draftIssuesAtom, selector));

  return draftViolations;
}

export function IntegrationRaptorDraftStatus() {
  const isEnabled = hasFeature('raptor_draft');
  const [queueLength] = useAtom(queueLengthAtom);
  const [errors] = useAtom(errorsAtom);

  if (!isEnabled) return null;
  return (
    <Box display="flex" alignItems="center">
      {queueLength > 0 && (
        <>
          <Text color="neutral.white_50" mr={2}>
            Fetching draft for {queueLength} port calls
          </Text>
          <LoadingSpinner size={16}></LoadingSpinner>
        </>
      )}
      {errors.length > 0 && (
        <>
          <Tooltip
            content={
              <div>
                {errors.map((err) => (
                  <div key={err.params.portCallId}>
                    <strong>{err.message}</strong>
                    <p>
                      <pre>
                        <small>
                          {JSON.stringify(err.params, undefined, 2)}
                        </small>
                      </pre>
                    </p>
                  </div>
                ))}
              </div>
            }
          >
            <Text color="status.error" isUnselectable>
              Draft API error
            </Text>
          </Tooltip>
        </>
      )}
    </Box>
  );
}

async function checkVesselSuggestions(
  vessel: Vessel,
  suggestions: Suggestion[],
  locationsById: Map<number, ScenarioLocation>,
) {
  const newSuggestions: Suggestion[] = [];

  const vesselImo = vessel.imo;
  const vesselDraft = vessel.draft;

  if (!vesselImo || !vesselDraft) return suggestions;

  for (const suggestion of suggestions) {
    /** Max mass for each location where vessel max draft exceeds port draft */
    const maxLoadedMassByLocation = new Map<number, number>();

    /*
     * We track used mass capacity after load events because
     * suggestion TimeData doesn't include port call events and thus
     * we don't have direct access to used mass capacity on arrival
     * to discharge locations
     */
    let currentLoadedMass = 0;
    suggestion.schedule?.timeData.forEach((td) => {
      if (isLoadEventTimeData(td)) {
        currentLoadedMass = td.usedMassCapacity;
      }

      if (!('locationId' in td.event)) return;
      const locationId = td.event.locationId;
      const location = locationsById.get(locationId);

      if (location?.draft && +vesselDraft > +location.draft) {
        maxLoadedMassByLocation.set(
          locationId,
          Math.max(
            maxLoadedMassByLocation.get(locationId) ?? 0,
            currentLoadedMass,
          ),
        );
      }
    });

    let draftExceeded = false;

    for (const [locationId, loadedMass] of maxLoadedMassByLocation) {
      try {
        const draft = await fetchRaptorDraft(+vesselImo, loadedMass);
        const location = locationsById.get(locationId);

        if (location?.draft && draft.fit > +location.draft) {
          draftExceeded = true;
          break;
        }
      } catch (err) {
        console.error('Error fetching suggestion draft', err);
      }
    }

    if (!draftExceeded) {
      newSuggestions.push(suggestion);
    }
  }

  return newSuggestions;
}

/**
 * Fetches predicted draft for potentially problematic suggestions and
 * filters out suggestions where predicted draft is larger than port draft
 *
 * Mutates the input SuggestionResult
 */
export async function integrationRaptorDraftFilterSuggestions(
  suggestionResult: SuggestionResult,
  vessels: ReadonlyArray<Vessel>,
  locationById: Map<number, ScenarioLocation>,
) {
  for (const [vesselId, suggestions] of suggestionResult.suggestionsByVessel) {
    const vessel = vessels.find((vessel) => vessel.id === vesselId);
    if (!vessel?.imo) return;

    const newSuggestions = await checkVesselSuggestions(
      vessel,
      suggestions,
      locationById,
    );

    suggestionResult.suggestionsByVessel.set(vesselId, newSuggestions);
  }
}
